import { NumericFormat } from 'react-number-format'
import {
  Button,
  ButtonList,
  ButtonListItem,
  RotatingChevron,
  ToggleInputButton,
} from './Button'
import type { ButtonListProps, ButtonProps } from './Button'
import { Checkbox } from './Checkbox'
import type { CheckboxProps } from './Checkbox'
import { Input } from './Input'
import type { InputProps } from './Input'
import { Label } from './Label'
import type { LabelProps } from './Label'
import { Popover, PopoverContent, PopoverTrigger } from './Popover'
import type { PopoverContentProps, PopoverProps } from './Popover'
import { RadioGroup, RadioGroupItem } from './RadioGroup'
import { Separator } from './Separator'
import { Switch } from './Switch'
import type { SwitchProps } from './Switch'
import { Calendar, DayPickerInput } from './Calendar'
import { FileUploadField } from './file-upload-field'
import type { FileUploadFieldProps } from './file-upload-field'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useIsAutoFilledFormField,
} from './Form'
import type { FormItemProps } from './Form'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './Select'
import type { SelectContentProps } from './Select'
import { Textarea } from './Textarea'
import { cn, formatDate, toDate, toPlainDate, tv } from '@joyful/utils'
import { CalendarIcon } from 'lucide-react'
import type {
  ComponentProps,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
} from 'react'
import type { PropsBase, PropsSingle } from 'react-day-picker'
import { useFormContext, useWatch } from 'react-hook-form'
import type { ControllerProps, FieldPath, FieldValues } from 'react-hook-form'
import type { VariantProps } from '@joyful/utils'
import type { SetRequired } from 'type-fest'
import { useControllableState } from '@radix-ui/react-use-controllable-state'

const DEFAULT_MAX_FILE_UPLOAD_SIZE_BYTES = 10485760

export interface FormFieldProps extends Pick<FormItemProps, 'orientation'> {
  autoFillDescription?: string
  className?: string
  description?: string | ReactElement
  isRequired?: boolean
  label?: ReactNode

  /**
   * @default false
   */
  shouldHideMessage?: boolean
}

type FormFieldLabelProps = LabelProps &
  Pick<FormFieldProps, 'autoFillDescription'>

export function FormFieldLabel({
  autoFillDescription,
  children,
  ...labelProps
}: FormFieldLabelProps) {
  const isAutoFilled = useIsAutoFilledFormField()
  return (
    <FormLabel
      endAdornment={
        isAutoFilled && autoFillDescription !== undefined ? (
          <p className="text-tremor-brand text-xs">{`📝 ${autoFillDescription}`}</p>
        ) : null
      }
      {...labelProps}
    >
      {children}
    </FormLabel>
  )
}

export interface FormInputFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>
  extends
    Omit<
      ControllerProps<TFieldValues, TName, TTransformedFieldValues>,
      'render'
    >,
    FormFieldProps {
  inputProps?: ComponentProps<typeof Input>
}

export function FormInputField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>({
  autoFillDescription,
  className,
  description,
  inputProps,
  isRequired,
  label,
  orientation,
  shouldHideMessage = false,
  ...controlProps
}: FormInputFieldProps<TFieldValues, TName, TTransformedFieldValues>) {
  return (
    <FormField
      render={({ field }) => {
        return (
          <FormItem className={className} orientation={orientation}>
            {label === undefined ? null : (
              <FormFieldLabel
                isRequired={isRequired}
                autoFillDescription={autoFillDescription}
              >
                {label}
              </FormFieldLabel>
            )}

            <FormControl>
              <Input
                {...field}
                {...inputProps}
                onBlur={(event) => {
                  inputProps?.onBlur?.(event)
                  field.onBlur()
                }}
                onChange={(event) => {
                  inputProps?.onChange?.(event)
                  field.onChange(event)
                }}
              />
            </FormControl>

            {typeof description === 'string' ? (
              <FormDescription>{description}</FormDescription>
            ) : (
              description
            )}

            {shouldHideMessage ? null : <FormMessage />}
          </FormItem>
        )
      }}
      {...controlProps}
    />
  )
}

export interface FormTextareaFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>
  extends
    Omit<
      ControllerProps<TFieldValues, TName, TTransformedFieldValues>,
      'render'
    >,
    FormFieldProps {
  textareaProps?: ComponentProps<typeof Textarea>
}

export function FormTextareaField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>({
  autoFillDescription,
  className,
  description,
  isRequired,
  label,
  orientation,
  shouldHideMessage = false,
  textareaProps,
  ...controlProps
}: FormTextareaFieldProps<TFieldValues, TName, TTransformedFieldValues>) {
  return (
    <FormField
      render={({ field }) => {
        return (
          <FormItem
            className={cn('items-stretch', className)}
            orientation={orientation}
          >
            {label === undefined ? null : (
              <FormFieldLabel
                isRequired={isRequired}
                autoFillDescription={autoFillDescription}
              >
                {label}
              </FormFieldLabel>
            )}

            <FormControl>
              <Textarea rows={3} {...field} {...textareaProps} />
            </FormControl>

            {typeof description === 'string' ? (
              <FormDescription>{description}</FormDescription>
            ) : (
              description
            )}

            {shouldHideMessage ? null : <FormMessage />}
          </FormItem>
        )
      }}
      {...controlProps}
    />
  )
}

export interface FormSelectOption<Value extends string> {
  disabled?: boolean
  label: string | ReactElement<unknown>
  labelAdornment?: ReactElement<unknown>
  startAdornment?: ReactElement<unknown>
  subtitle?: string
  value: Value
}

export interface FormSelectFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Value extends string,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>
  extends
    Omit<
      ControllerProps<TFieldValues, TName, TTransformedFieldValues>,
      'render'
    >,
    FormFieldProps {
  onValueChange?: (value: string) => void
  options: Array<FormSelectOption<Value>>
  placeholder: string
  selectContentProps?: Omit<SelectContentProps, 'children'>
  triggerButtonProps?: Omit<ButtonProps, 'children' | 'endIcon'>
  triggerClassName?: string
}

export function FormSelectField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Value extends string,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>({
  autoFillDescription,
  className,
  description,
  isRequired,
  label,
  onValueChange,
  options,
  orientation,
  placeholder,
  selectContentProps,
  shouldHideMessage = false,
  triggerButtonProps,
  triggerClassName,
  ...controlProps
}: FormSelectFieldProps<TFieldValues, TName, Value, TTransformedFieldValues>) {
  return (
    <FormField
      render={({ field: { onChange, ref: _, ...field } }) => {
        return (
          <FormItem className={className} orientation={orientation}>
            {label === undefined ? null : (
              <FormFieldLabel
                isRequired={isRequired}
                autoFillDescription={autoFillDescription}
              >
                {label}
              </FormFieldLabel>
            )}

            <Select
              onValueChange={(value) => {
                onChange(value)
                onValueChange?.(value)
              }}
              {...field}
            >
              <FormControl>
                <SelectTrigger
                  className={triggerClassName}
                  buttonProps={{
                    width: 'full',
                    ...triggerButtonProps,
                  }}
                >
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
              </FormControl>
              <SelectContent {...selectContentProps}>
                <SelectGroup>
                  {typeof label === 'string' ? (
                    <SelectLabel>{label}</SelectLabel>
                  ) : null}
                  {options.map((option) => {
                    return (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        startAdornment={option.startAdornment}
                        subtitle={option.subtitle}
                        disabled={option.disabled}
                        labelAdornment={option.labelAdornment}
                      >
                        {option.label}
                      </SelectItem>
                    )
                  })}
                </SelectGroup>
              </SelectContent>
            </Select>

            {typeof description === 'string' ? (
              <FormDescription>{description}</FormDescription>
            ) : (
              description
            )}

            {shouldHideMessage ? null : <FormMessage />}
          </FormItem>
        )
      }}
      {...controlProps}
    />
  )
}

interface FormSelectOrOtherFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TOtherName extends FieldPath<TFieldValues>,
  Value extends string,
  TTransformedFieldValues extends FieldValues = TFieldValues,
> extends FormSelectFieldProps<
  TFieldValues,
  TName,
  Value,
  TTransformedFieldValues
> {
  otherFieldProps: Pick<
    FormInputFieldProps<TFieldValues, TOtherName, TTransformedFieldValues>,
    'name' | 'inputProps' | 'label'
  >
  otherValue: Value
}

export function FormSelectOrOtherField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TOtherName extends FieldPath<TFieldValues>,
  Value extends string,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>({
  control,
  name,
  otherFieldProps,
  otherValue,
  ...formSelectFieldProps
}: FormSelectOrOtherFieldProps<
  TFieldValues,
  TName,
  TOtherName,
  Value,
  TTransformedFieldValues
>) {
  const form = useFormContext<TFieldValues>()
  const watchedValue = useWatch({
    control: form.control,
    name,
  })

  return (
    <div className="flex flex-col gap-2">
      <FormSelectField
        {...formSelectFieldProps}
        name={name}
        control={control}
        onValueChange={(value) => {
          formSelectFieldProps.onValueChange?.(value)

          if (value === otherValue) {
            setTimeout(() => {
              form.setFocus(otherFieldProps.name)
            }, 50)
          }
        }}
      />

      {watchedValue === 'other' ? (
        <FormInputField
          control={form.control}
          isRequired
          className="animate-in fade-in slide-in-from-top"
          label={
            formSelectFieldProps.label === undefined ? undefined : (
              <>
                {formSelectFieldProps.label}
                {` (Other)`}
              </>
            )
          }
          autoFillDescription={formSelectFieldProps.autoFillDescription}
          {...otherFieldProps}
        />
      ) : null}
    </div>
  )
}

export interface FormDateFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>
  extends
    Omit<
      ControllerProps<TFieldValues, TName, TTransformedFieldValues>,
      'render'
    >,
    FormFieldProps {
  buttonProps?: Partial<Omit<ButtonProps, 'children'>>
  calendarHeader?: ReactElement

  calendarProps?: Partial<Omit<PropsBase & PropsSingle, 'selected'>>

  /**
   * @default false
   */
  canClearDate?: boolean

  /**
   * @default 'default'
   */
  controlWidth?: 'default' | 'none'

  /**
   * @default false
   */
  isDatePickerDisabled?: boolean

  isPopoverOpen?: boolean

  /**
   * @default 'Select date'
   */
  placeholder?: string
  popoverContentProps?: Omit<PopoverContentProps, 'children'>
  popoverProps?: Omit<PopoverProps, 'children' | 'open' | 'onOpenChange'>
  setPopoverOpen?: Dispatch<SetStateAction<boolean>>
}

export function FormDateField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>({
  buttonProps,
  calendarHeader,
  calendarProps,
  canClearDate = false,
  className,
  controlWidth = 'default',
  description,
  isDatePickerDisabled = false,
  isPopoverOpen: isPopoverOpenProp,
  isRequired,
  label,
  orientation,
  placeholder = 'Select date',
  popoverContentProps,
  popoverProps,
  setPopoverOpen: setPopoverOpenProp,
  shouldHideMessage = false,
  ...controlProps
}: FormDateFieldProps<TFieldValues, TName, TTransformedFieldValues>) {
  const [isPopoverOpen, setPopoverOpen] = useControllableState({
    defaultProp: isPopoverOpenProp ?? false,
    onChange: setPopoverOpenProp,
    prop: isPopoverOpenProp,
  })

  return (
    <FormField
      render={({ field }) => {
        const parsedDate = toDate(field.value) ?? undefined

        return (
          <FormItem className={className} orientation={orientation}>
            {label === undefined ? null : (
              <FormLabel isRequired={isRequired}>{label}</FormLabel>
            )}

            <Popover
              {...popoverProps}
              open={isPopoverOpen}
              onOpenChange={setPopoverOpen}
            >
              <PopoverTrigger asChild>
                <FormControl
                  className={
                    controlWidth === 'default' ? 'min-w-60' : 'min-w-0'
                  }
                >
                  <Button
                    align="between"
                    startIcon={<CalendarIcon />}
                    endIcon={
                      <RotatingChevron className="text-tremor-content size-3" />
                    }
                    colorVariant={field.value ? 'default' : 'subtle'}
                    weight={field.value ? 'medium' : 'normal'}
                    width="full"
                    {...buttonProps}
                  >
                    <span className="flex-[1_0_0] text-start">
                      {field.value
                        ? formatDate(field.value, {
                            shouldSkipCurrentYear: true,
                          })
                        : placeholder}
                    </span>
                  </Button>
                </FormControl>
              </PopoverTrigger>

              <PopoverContent padding="none" {...popoverContentProps}>
                {calendarHeader}

                <DayPickerInput
                  className="max-w-32 px-3 py-2"
                  disabled={isDatePickerDisabled}
                  id={`${field.name}-date`}
                  value={field.value ?? null}
                  onChange={(value) => {
                    field.onChange(value?.toString() ?? undefined)
                    if (value) {
                      setPopoverOpen(false)
                    }
                  }}
                />

                <Separator />

                <div
                  role="presentation"
                  onMouseDown={(event) => {
                    // prevent mousedown default to avoid focus-stealing when clicking calendar days
                    // this allows the input to keep focus while still triggering selection on first click
                    event.preventDefault()
                  }}
                >
                  <Calendar
                    mode="single"
                    defaultMonth={parsedDate}
                    {...calendarProps}
                    disabled={
                      isDatePickerDisabled ? true : calendarProps?.disabled
                    }
                    selected={parsedDate}
                    onSelect={(value, ...rest) => {
                      // Blur input first so its onBlur handler fires before we update the value
                      // This prevents the input's onBlur from overwriting our calendar selection
                      if (document.activeElement instanceof HTMLElement) {
                        document.activeElement.blur()
                      }

                      field.onChange(
                        toPlainDate(value)?.toString() ?? undefined,
                      )
                      calendarProps?.onSelect?.(value, ...rest)
                      setPopoverOpen(false)
                    }}
                  />
                </div>

                {canClearDate && field.value ? (
                  <footer className="self-stretch border-t p-1.5">
                    <Button
                      width="full"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        field.onChange(null)
                        setPopoverOpen(false)
                      }}
                    >
                      {`Clear date`}
                    </Button>
                  </footer>
                ) : null}
              </PopoverContent>
            </Popover>

            {typeof description === 'string' ? (
              <FormDescription>{description}</FormDescription>
            ) : (
              description
            )}

            {shouldHideMessage ? null : <FormMessage />}
          </FormItem>
        )
      }}
      {...controlProps}
    />
  )
}

export interface MoneyAmountInputProps {
  readonly currency: string

  /**
   * @default false
   */
  readonly shouldNotifyChangeOnBlur?: boolean

  /**
   * @default false
   */
  readonly shouldNotifyChangeOnEnter?: boolean
  readonly value: number | null
}

export type MoneyInputProps = InputProps &
  Omit<ComponentProps<typeof NumericFormat>, 'size'>

export interface FormMoneyAmountInputFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>
  extends
    Omit<
      ControllerProps<TFieldValues, TName, TTransformedFieldValues>,
      'render'
    >,
    FormFieldProps {
  /**
   * @default 'decimal'
   */
  readonly amountType?: 'decimal' | 'number'
  readonly currency: string
  inputProps?: Omit<MoneyInputProps, 'value' | 'onValueChange'>
}

export function MoneyInput({ ref, ...props }: MoneyInputProps) {
  return (
    <NumericFormat
      decimalScale={2}
      fixedDecimalScale
      prefix="$"
      decimalSeparator="."
      thousandSeparator=","
      allowNegative={false}
      // @ts-expect-error - react-number-format types are not correct
      customInput={Input}
      placeholder="$0.00"
      getInputRef={ref}
      fontVariant="tabular"
      {...props}
    />
  )
}

export function FormMoneyAmountInputField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>({
  className,
  description,
  inputProps,
  isRequired,
  label,
  orientation,
  shouldHideMessage = false,
  ...fieldProps
}: FormMoneyAmountInputFieldProps<
  TFieldValues,
  TName,
  TTransformedFieldValues
>) {
  return (
    <FormField
      {...fieldProps}
      render={({ field }) => {
        return (
          <FormItem className={className} orientation={orientation}>
            {label !== undefined && (
              <FormLabel isRequired={isRequired}>{label}</FormLabel>
            )}
            <FormControl>
              <MoneyInput
                {...inputProps}
                className={cn('w-auto min-w-60', inputProps?.className)}
                value={field.value}
                onValueChange={(values) => {
                  field.onChange(values.floatValue)
                }}
              />
            </FormControl>
            {typeof description === 'string' ? (
              <FormDescription>{description}</FormDescription>
            ) : (
              description
            )}
            {shouldHideMessage ? null : <FormMessage />}
          </FormItem>
        )
      }}
    />
  )
}

type NumberInputProps = InputProps &
  Omit<ComponentProps<typeof NumericFormat>, 'size'>

function NumberInput({ ref, ...props }: NumberInputProps) {
  return (
    <NumericFormat
      decimalSeparator="."
      thousandSeparator=","
      allowNegative={false}
      // @ts-expect-error - react-number-format types are not correct
      customInput={Input}
      placeholder="0"
      getInputRef={ref}
      fontVariant="tabular"
      {...props}
    />
  )
}

export interface FormNumberInputFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>
  extends
    Omit<
      ControllerProps<TFieldValues, TName, TTransformedFieldValues>,
      'render'
    >,
    FormFieldProps {
  inputProps?: Omit<NumberInputProps, 'value' | 'onValueChange'>
  labelProps?: Partial<LabelProps>
}

export function FormNumberInputField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>({
  className,
  description,
  inputProps,
  isRequired,
  label,
  labelProps,
  orientation,
  shouldHideMessage = false,
  ...fieldProps
}: FormNumberInputFieldProps<TFieldValues, TName, TTransformedFieldValues>) {
  return (
    <FormField
      {...fieldProps}
      render={({ field }) => {
        return (
          <FormItem className={className} orientation={orientation}>
            {label !== undefined && (
              <FormLabel isRequired={isRequired} {...labelProps}>
                {label}
              </FormLabel>
            )}
            <FormControl>
              <NumberInput
                {...inputProps}
                className={cn('w-auto min-w-60', inputProps?.className)}
                value={field.value}
                onValueChange={(values) => {
                  field.onChange(values.floatValue)
                }}
              />
            </FormControl>
            {description !== undefined && (
              <FormDescription>{description}</FormDescription>
            )}
            {shouldHideMessage ? null : <FormMessage />}
          </FormItem>
        )
      }}
    />
  )
}

export type FormSwitchFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
> = Omit<
  ControllerProps<TFieldValues, TName, TTransformedFieldValues>,
  'render'
> &
  Omit<SetRequired<FormFieldProps, 'label'>, 'isRequired'> & {
    labelProps?: Partial<LabelProps>
    layout?: 'inline' | 'row'
    switchProps?: Partial<Omit<SwitchProps, 'checked'>>
  }

export function FormSwitchField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>({
  className,
  description,
  label,
  labelProps,
  layout = 'inline',
  orientation,
  shouldHideMessage: _,
  switchProps,
  ...fieldProps
}: FormSwitchFieldProps<TFieldValues, TName, TTransformedFieldValues>) {
  return (
    <FormField
      {...fieldProps}
      render={({ field }) => {
        const switchControl = (
          <FormControl>
            <Switch
              // oxlint-disable-next-line joyful/prefer-boolean-check -- form values may be undefined
              checked={field.value === true}
              {...switchProps}
              onCheckedChange={(checked) => {
                field.onChange(checked)
                switchProps?.onCheckedChange?.(checked)
              }}
            />
          </FormControl>
        )

        return (
          <FormItem className={className} orientation={orientation}>
            {layout === 'row' ? (
              <div className="flex w-full items-center justify-between gap-x-4">
                <div className="flex min-w-0 flex-col gap-y-1">
                  <FormLabel {...labelProps}>{label}</FormLabel>
                  {description !== undefined && (
                    <FormDescription>{description}</FormDescription>
                  )}
                </div>
                {switchControl}
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  {switchControl}
                  <FormLabel {...labelProps}>{label}</FormLabel>
                </div>
                {description !== undefined && (
                  <FormDescription>{description}</FormDescription>
                )}
              </>
            )}
          </FormItem>
        )
      }}
    />
  )
}

export type FormCheckboxFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
> = Omit<
  ControllerProps<TFieldValues, TName, TTransformedFieldValues>,
  'render'
> &
  Omit<SetRequired<FormFieldProps, 'label'>, 'isRequired'> & {
    checkboxProps?: Partial<Omit<CheckboxProps, 'checked'>>
    labelProps?: Partial<LabelProps>
  }

export function FormCheckboxField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>({
  checkboxProps,
  className,
  description,
  label,
  labelProps,
  orientation,
  shouldHideMessage: _,
  ...fieldProps
}: FormCheckboxFieldProps<TFieldValues, TName, TTransformedFieldValues>) {
  return (
    <FormField
      {...fieldProps}
      render={({ field }) => {
        return (
          <FormItem className={className} orientation={orientation}>
            <div className="flex flex-row items-center gap-2">
              <FormControl>
                <Checkbox
                  checked={field.value === true} // oxlint-disable-line joyful/prefer-boolean-check -- form values may be undefined
                  {...checkboxProps}
                  onCheckedChange={(checked) => {
                    field.onChange(checked)
                    checkboxProps?.onCheckedChange?.(checked)
                  }}
                />
              </FormControl>
              <FormLabel {...labelProps}>{label}</FormLabel>
            </div>
            {typeof description === 'string' ? (
              <FormDescription>{description}</FormDescription>
            ) : (
              description
            )}
          </FormItem>
        )
      }}
    />
  )
}

const checkboxGroupVariants = tv({
  defaultVariants: {
    layout: 'inline',
  },

  variants: {
    layout: {
      grid: 'grid grid-cols-2 gap-3',
      inline: 'inline-flex flex-wrap items-center gap-x-4 gap-y-2',
      vertical: 'flex flex-col gap-y-2',
    },
  },
})

interface FormCheckboxGroupFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>
  extends
    Omit<
      ControllerProps<TFieldValues, TName, TTransformedFieldValues>,
      'render'
    >,
    FormFieldProps,
    VariantProps<typeof checkboxGroupVariants> {
  className?: string
  options: Array<{
    accessibilityLabel?: string
    disabled?: boolean
    endContent?: ReactNode
    label: ReactNode
    value: string
  }>
}

export function FormCheckboxGroupField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>({
  className,
  description,
  isRequired,
  label,
  layout,
  options,
  orientation,
  shouldHideMessage,
  ...fieldProps
}: FormCheckboxGroupFieldProps<TFieldValues, TName, TTransformedFieldValues>) {
  return (
    <FormField
      {...fieldProps}
      render={({ field }) => {
        const values: Array<string> = Array.isArray(field.value)
          ? field.value
          : []
        return (
          <FormItem
            className={cn('gap-y-3', className)}
            orientation={orientation}
          >
            {label !== undefined && (
              <FormLabel isRequired={isRequired}>{label}</FormLabel>
            )}

            <FormControl>
              <div
                className={checkboxGroupVariants({
                  layout,
                })}
              >
                {options.map((option) => {
                  return (
                    <div
                      key={option.value}
                      className="flex w-full items-center gap-2"
                    >
                      <Checkbox
                        id={option.value}
                        aria-label={
                          option.label ? undefined : option.accessibilityLabel
                        }
                        checked={values.includes(option.value)}
                        disabled={option.disabled}
                        onCheckedChange={(checked) => {
                          // oxlint-disable-next-line joyful/prefer-boolean-check -- skip indeterminate
                          if (checked === true) {
                            field.onChange(
                              options
                                .map((candidate) => {
                                  return candidate.value
                                })
                                .filter((value) => {
                                  return (
                                    value === option.value ||
                                    values.includes(value)
                                  )
                                }),
                            )
                          } else {
                            field.onChange(
                              values.filter((val) => {
                                return val !== option.value
                              }),
                            )
                          }
                        }}
                      />
                      {option.label ? (
                        <Label htmlFor={option.value} size="sm">
                          {option.label}
                        </Label>
                      ) : null}
                      {option.endContent}
                    </div>
                  )
                })}
              </div>
            </FormControl>
            {description !== undefined && (
              <FormDescription>{description}</FormDescription>
            )}
            {shouldHideMessage ? null : <FormMessage />}
          </FormItem>
        )
      }}
    />
  )
}

const radioGroupVariants = tv({
  base: 'gap-y-3',

  slots: {
    item: 'flex items-center gap-2',
  },
})

interface FormRadioGroupFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>
  extends
    Omit<
      ControllerProps<TFieldValues, TName, TTransformedFieldValues>,
      'render'
    >,
    FormFieldProps {
  buttonListProps?: Partial<ButtonListProps>
  className?: string
  onValueChange?: (value: string) => void
  options: Array<{
    accessibilityLabel?: string
    endContent?: ReactNode
    label: ReactNode
    value: string
  }>

  /**
   * @default 'sm'
   */
  size?: LabelProps['size']

  /**
   * @default 'radio'
   */
  variant?: 'radio' | 'button'
}

export function FormRadioGroupField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>({
  buttonListProps,
  className,
  description,
  isRequired,
  label,
  onValueChange,
  options,
  orientation,
  shouldHideMessage = false,
  size = 'sm',
  variant = 'radio',
  ...fieldProps
}: FormRadioGroupFieldProps<TFieldValues, TName, TTransformedFieldValues>) {
  const classNames = radioGroupVariants()

  return (
    <FormField
      {...fieldProps}
      render={({ field }) => {
        return (
          <FormItem
            className={cn(classNames.base(), className)}
            orientation={orientation}
          >
            {label !== undefined && (
              <FormLabel isRequired={isRequired}>{label}</FormLabel>
            )}

            <FormControl>
              <RadioGroup
                className="w-full"
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value)
                  onValueChange?.(value)
                }}
              >
                {variant === 'radio'
                  ? options.map((option) => {
                      return (
                        <div
                          key={option.value}
                          className={cn(classNames.item(), 'w-full')}
                        >
                          <RadioGroupItem
                            id={option.value}
                            aria-label={
                              option.label
                                ? undefined
                                : option.accessibilityLabel
                            }
                            value={option.value}
                          />
                          {option.label ? (
                            <Label htmlFor={option.value} size={size}>
                              {option.label}
                            </Label>
                          ) : null}
                          {option.endContent}
                        </div>
                      )
                    })
                  : null}

                {variant === 'button' ? (
                  <ButtonList {...buttonListProps}>
                    {options.map((option) => {
                      const isSelected = option.value === field.value
                      const buttonLabel = option.label || option.value
                      return (
                        <ButtonListItem key={option.value}>
                          <ToggleInputButton
                            isSelected={isSelected}
                            variant="radio"
                            size="lg"
                            onClick={() => {
                              field.onChange(option.value)
                              onValueChange?.(option.value)
                            }}
                          >
                            {buttonLabel}
                          </ToggleInputButton>
                        </ButtonListItem>
                      )
                    })}
                  </ButtonList>
                ) : null}
              </RadioGroup>
            </FormControl>
            {typeof description === 'string' ? (
              <FormDescription>{description}</FormDescription>
            ) : (
              description
            )}
            {shouldHideMessage ? null : <FormMessage />}
          </FormItem>
        )
      }}
    />
  )
}

export type FormFileUploadFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = Omit<ControllerProps<TFieldValues, TName>, 'render'> &
  Omit<FormFieldProps, 'isRequired'> &
  Pick<
    FileUploadFieldProps,
    | 'dropzone'
    | 'isUploading'
    | 'maxFiles'
    | 'maxSize'
    | 'multiple'
    | 'onUpload'
    | 'removeFile'
    | 'rejectedFiles'
  >

export function FormFileUploadField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  className,
  control,
  dropzone,
  isUploading,
  label,
  maxFiles = 5,
  maxSize = DEFAULT_MAX_FILE_UPLOAD_SIZE_BYTES,
  multiple,
  onUpload,
  orientation,
  rejectedFiles,
  removeFile,
  shouldHideMessage = false,
  ...controlProps
}: FormFileUploadFieldProps<TFieldValues, TName>) {
  return (
    <FormField
      control={control}
      {...controlProps}
      render={({ field, fieldState }) => {
        return (
          <FormItem className={className} orientation={orientation}>
            <FileUploadField
              name={field.name}
              dropzone={dropzone}
              label={label}
              files={field.value}
              onChange={field.onChange}
              onUpload={onUpload}
              isUploading={isUploading}
              error={fieldState.error?.message}
              disabled={field.disabled}
              multiple={multiple}
              maxSize={maxSize}
              maxFiles={maxFiles}
              removeFile={removeFile}
              rejectedFiles={rejectedFiles}
            />
            {shouldHideMessage ? null : <FormMessage />}
          </FormItem>
        )
      }}
    />
  )
}
